import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';
import { Lock, Unlock, RefreshCw, X } from 'lucide-react';
import UserDetailsEditor from './UserDetailsEditor';
import { formatDateTime } from '../utils/datetime';

const LockerAssignModal = ({ open, onClose, eventId, userId }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const [viewAll, setViewAll] = useState(false);
  const [selectedToAssign, setSelectedToAssign] = useState([]);
  const [selectedToFree, setSelectedToFree] = useState([]);

  const enabled = open && !!eventId && !!userId;

  // Live locker metrics (total / assigned / free) — polled every 2s.
  const { data: summary, refetch: refetchSummary } = useQuery({
    queryKey: ['lockerSummary', eventId],
    queryFn: () => ApiService.getLockerSummary(eventId),
    enabled,
    refetchInterval: 2000,
  });

  // Available lockers — polled every 2s so admins see a fresh, contention-aware list.
  const { data: availableData, isFetching: availableFetching, refetch: refetchAvailable } = useQuery({
    queryKey: ['availableLockers', eventId, viewAll],
    queryFn: () => ApiService.getAvailableLockers(eventId, { limit: 15, all: viewAll }),
    enabled,
    refetchInterval: 2000,
  });

  const { data: assignedLockers = [], refetch: refetchAssigned } = useQuery({
    queryKey: ['userLockers', eventId, userId],
    queryFn: () => ApiService.getUserLockers(eventId, userId),
    enabled,
  });

  const { data: history = [], refetch: refetchHistory } = useQuery({
    queryKey: ['lockerHistory', eventId, userId],
    queryFn: () => ApiService.getUserLockerHistory(eventId, userId),
    enabled,
  });

  // User registration + event form schema, so missing fields (contact/email/…)
  // can be filled in right here while assigning a locker.
  const { data: registration } = useQuery({
    queryKey: ['registration', eventId, userId],
    queryFn: () => ApiService.getRegistration(eventId, userId),
    enabled,
  });

  const { data: event } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => ApiService.getEvent(eventId),
    enabled: open && !!eventId,
  });

  const updateRegistrationMutation = useMutation({
    mutationFn: (formData) => ApiService.updateRegistration(eventId, userId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['registration', eventId, userId]);
      queryClient.invalidateQueries(['registrations', eventId]);
      showSuccess('User details updated successfully!');
    },
    onError: (e) => showError(e.message || 'Failed to update user details'),
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries(['availableLockers', eventId]);
    queryClient.invalidateQueries(['userLockers', eventId, userId]);
    queryClient.invalidateQueries(['lockerHistory', eventId, userId]);
    queryClient.invalidateQueries(['lockerSummary', eventId]);
    queryClient.invalidateQueries(['registrations', eventId]);
  };

  const forceRefresh = () => {
    refetchSummary();
    refetchAvailable();
    refetchAssigned();
    refetchHistory();
  };

  const assignMutation = useMutation({
    mutationFn: (numbers) => ApiService.assignLockers(eventId, userId, numbers),
    onSuccess: (assigned) => {
      showSuccess(`Assigned locker(s): ${assigned.join(', ')}`);
      setSelectedToAssign([]);
      invalidateAll();
    },
    onError: (e) => showError(e.message || 'Failed to assign lockers'),
  });

  const freeMutation = useMutation({
    mutationFn: (numbers) => ApiService.freeLockers(eventId, userId, numbers),
    onSuccess: (freed) => {
      showSuccess(`Freed locker(s): ${freed.join(', ')}`);
      setSelectedToFree([]);
      invalidateAll();
    },
    onError: (e) => showError(e.message || 'Failed to free lockers'),
  });

  const toggle = (list, setList, value) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const handleClose = () => {
    setSelectedToAssign([]);
    setSelectedToFree([]);
    setViewAll(false);
    onClose();
  };

  const available = availableData?.available ?? [];
  const totalAvailable = availableData?.totalAvailable ?? 0;
  const busy = assignMutation.isPending || freeMutation.isPending;

  const metrics = [
    { label: 'Total', value: summary?.total, tone: 'text-gray-900' },
    { label: 'Assigned', value: summary?.assigned, tone: 'text-amber-600' },
    { label: 'Free', value: summary?.available, tone: 'text-green-600' },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className="max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <DialogHeader showClose={false} className="p-0 border-0">
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle>Manage Lockers</DialogTitle>
              <DialogDescription>
                Assign or free lockers for registration {userId}.
              </DialogDescription>
            </div>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-600 transition-colors -mt-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </DialogHeader>

        {/* Live metrics + force refresh */}
        <div className="mt-2 flex items-stretch gap-3">
          {metrics.map((m) => (
            <div key={m.label} className="flex-1 rounded-lg border p-3 text-center">
              <div className={`text-2xl font-semibold ${m.tone}`}>{m.value ?? '—'}</div>
              <div className="text-xs text-gray-500">{m.label} lockers</div>
            </div>
          ))}
          <button
            type="button"
            onClick={forceRefresh}
            title="Refresh locker list"
            className="flex flex-col items-center justify-center gap-1 rounded-lg border px-3 text-gray-600 hover:bg-gray-50"
          >
            <RefreshCw className={`w-4 h-4 ${availableFetching ? 'animate-spin' : ''}`} />
            <span className="text-xs">Refresh</span>
          </button>
        </div>

        {/* Editable user details — fill in missing contact/email/etc. while assigning */}
        {registration && (
          <div className="mt-4">
            <UserDetailsEditor
              userData={registration}
              formSchema={event?.formSchema || []}
              onSave={(formData) => updateRegistrationMutation.mutate(formData)}
              isLoading={updateRegistrationMutation.isPending}
            />
          </div>
        )}

        <hr className="my-4" />

        {/* Currently assigned / free */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Assigned to this user</span>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || selectedToFree.length === 0}
              onClick={() => freeMutation.mutate(selectedToFree)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Unlock className="w-3 h-3 mr-1" />
              Free selected ({selectedToFree.length})
            </Button>
          </div>
          {assignedLockers.length === 0 ? (
            <p className="text-sm text-gray-400">No lockers assigned yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {assignedLockers.map((num) => {
                const active = selectedToFree.includes(num);
                return (
                  <button
                    key={num}
                    type="button"
                    onClick={() => toggle(selectedToFree, setSelectedToFree, num)}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      active
                        ? 'bg-red-100 border-red-400 text-red-700'
                        : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {num}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <hr className="my-4" />

        {/* Available to assign */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium flex items-center gap-1">
              Available lockers
              <RefreshCw className={`w-3 h-3 text-gray-400 ${availableFetching ? 'animate-spin' : ''}`} />
              <span className="text-gray-400 font-normal">({totalAvailable} total)</span>
            </span>
            <Button
              size="sm"
              disabled={busy || selectedToAssign.length === 0}
              onClick={() => assignMutation.mutate(selectedToAssign)}
            >
              <Lock className="w-3 h-3 mr-1" />
              Assign selected ({selectedToAssign.length})
            </Button>
          </div>

          {available.length === 0 ? (
            <p className="text-sm text-gray-400">No lockers available.</p>
          ) : (
            <div className="flex flex-wrap gap-2 max-h-52 overflow-y-auto">
              {available.map((num) => {
                const active = selectedToAssign.includes(num);
                return (
                  <button
                    key={num}
                    type="button"
                    onClick={() => toggle(selectedToAssign, setSelectedToAssign, num)}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      active
                        ? 'bg-indigo-100 border-indigo-400 text-indigo-700'
                        : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {num}
                  </button>
                );
              })}
            </div>
          )}

          {!viewAll && availableData?.limited && (
            <button
              type="button"
              className="text-sm text-indigo-600 hover:underline"
              onClick={() => setViewAll(true)}
            >
              View all {totalAvailable} available lockers
            </button>
          )}
          {viewAll && (
            <button
              type="button"
              className="text-sm text-gray-500 hover:underline"
              onClick={() => setViewAll(false)}
            >
              Show fewer
            </button>
          )}
        </div>

        <hr className="my-4" />

        {/* History */}
        <div className="space-y-2">
          <span className="text-sm font-medium">Assignment history</span>
          {history.length === 0 ? (
            <p className="text-sm text-gray-400">No history yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-1 pr-3">Locker</th>
                    <th className="py-1 pr-3">Assigned at</th>
                    <th className="py-1 pr-3">By</th>
                    <th className="py-1 pr-3">Freed at</th>
                    <th className="py-1 pr-3">By</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={`${h.lockerNumber}-${i}`} className="border-b last:border-0">
                      <td className="py-1 pr-3 font-medium">{h.lockerNumber}</td>
                      <td className="py-1 pr-3">{formatDateTime(h.assignedAt)}</td>
                      <td className="py-1 pr-3 text-gray-600">{h.assignedBy || '—'}</td>
                      <td className="py-1 pr-3">
                        {h.freedAt ? formatDateTime(h.freedAt) : <span className="text-green-600">Active</span>}
                      </td>
                      <td className="py-1 pr-3 text-gray-600">{h.freedBy || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LockerAssignModal;
